import {createContext, Dispatch, PropsWithChildren, useContext, useEffect, useState} from "react";
import {useSessionMetadataContext} from "@/Context/SessionMetadataContext";
import {getEvaluationChanges} from "@/services/SurveyService";

/**
 * Where a review replaced an answer the crew gave.
 *
 * A review writes an ordinary response and marks the one before it superseded, so everything that reads
 * responses shows the review's answer and drops the crew's. These are what put the crew's back within reach,
 * so the session can mark the answers a review changed and show either side of one.
 */
export type EvaluationChange = {
    indicator_id: number
    node_index: number | null
    changed: boolean
    original: {input: string | null, user: string | null, at: string | null}
    evaluation: {input: string | null, user: string | null, at: string | null}
}

/** Which answer the session shows where a review replaced one. */
export type AnswersShown = 'reviewed' | 'crew'

type EvaluationChangesContextProps = {
    changes: EvaluationChange[]
    answersShown: AnswersShown
    setAnswersShown: Dispatch<AnswersShown>
    changeFor: (indicatorId: number, nodeIndex?: number | null) => EvaluationChange | undefined
}

const EvaluationChangesContext = createContext<EvaluationChangesContextProps | undefined>(undefined)

/**
 * The change recorded against one question in one sector, if a review replaced its answer.
 *
 * A question outside a repeating section carries no index; one inside it carries the sector's, so both have
 * to match or a change in sector two would mark sector one as well.
 */
export function findChange(
    changes: EvaluationChange[], indicatorId: number, nodeIndex?: number | null
): EvaluationChange | undefined {
    return changes.find(change =>
        change.indicator_id === indicatorId && (change.node_index ?? 0) === (nodeIndex ?? 0))
}

export const EvaluationChangesContextProvider = ({children}: PropsWithChildren) => {
    const {session, mode} = useSessionMetadataContext()

    const [changes, setChanges] = useState<EvaluationChange[]>([])
    const [answersShown, setAnswersShown] = useState<AnswersShown>('reviewed')

    // Only the evaluate and review screens have anything to say about a review; while responding there is
    // nothing to compare against yet.
    const wanted = !!session?.id && (mode === 'evaluate' || mode === 'view')

    useEffect(() => {
        if(!wanted) {
            setChanges([])

            return
        }

        let live = true
        getEvaluationChanges(session!)
            .then(fetched => {
                if(live) setChanges(fetched)
            })
            .catch(() => {
                if(live) setChanges([])
            })

        return () => {
            live = false
        }
    }, [session?.id, wanted])

    const value: EvaluationChangesContextProps = {
        changes,
        answersShown,
        setAnswersShown,
        changeFor: (indicatorId, nodeIndex) => findChange(changes, indicatorId, nodeIndex),
    }

    return <EvaluationChangesContext.Provider value={value}>
        {children}
    </EvaluationChangesContext.Provider>
}

export const useEvaluationChangesContext = (): EvaluationChangesContextProps => {
    const context = useContext(EvaluationChangesContext)

    if(!context) {
        throw new Error('useEvaluationChangesContext must be used within an EvaluationChangesContextProvider')
    }

    return context
}
